import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Search, TicketPercent } from "lucide-react";
import {
  COUPON_KINDS,
  CURRENCIES,
  couponInputSchema,
  formatPrice,
  type Coupon,
  type CouponKind,
  type Currency,
} from "@nh/shared";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Badge, Button, Input, Select } from "@/shared/ui/core";
import { EmptyState, Modal, SkeletonTable } from "@/shared/ui/ui";
import {
  AdminGuard,
  DataTable,
  DataTableFrame,
  FilterBar,
  Pagination,
  Td,
  Th,
  Tr,
  DEFAULT_PER_PAGE,
} from "@/features/admin/components";
import { useCoupons, useCreateCoupon, useSetCouponActive } from "./coupons.queries";

const KIND_LABELS: Record<CouponKind, string> = {
  percent: "Porcentaje",
  fixed: "Monto fijo",
};

/**
 * ═══════════════════════════════════════════════════════════════════
 * Cupones — réplica de `shop/admin/coupons` de producción (Anahí,
 * 27-ago). El panel crea el cupón; el cliente lo teclea al final del
 * checkout y el SERVIDOR calcula el descuento.
 *
 * El alta va en un MODAL, como en Sedes: la pantalla es el libro de
 * cupones, y "Nuevo cupón" abre el formulario encima. Un cupón no se
 * edita ni se borra: se APAGA — un código ya repartido es una promesa
 * con nombre.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminCouponsPage() {
  return (
    <AdminGuard>
      <CouponsScreen />
    </AdminGuard>
  );
}


function CouponsScreen() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  /*
   * Las filas por página las elige quien mira (Anahí, 29-ago): 5, 10 o 20,
   * con CINCO por defecto. Vive en el estado y no como constante porque
   * ahora cambia en caliente; al cambiarlo se vuelve a la página 1, que es
   * lo único sensato — la página 4 de 5 en 5 no es la página 4 de 20 en 20.
   */
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  // Filtros y paginación de SERVIDOR: solo viaja la página visible.
  const coupons = useCoupons({
    page,
    perPage: perPage,
    ...(search.trim() ? { query: search.trim() } : {}),
    ...(status ? { status: status as "active" | "inactive" } : {}),
  });
  const crear = useCreateCoupon();
  const apagar = useSetCouponActive();

  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [kind, setKind] = useState<CouponKind>("percent");
  const [percent, setPercent] = useState("10");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState<Currency>("MXN");
  const [expiresAt, setExpiresAt] = useState("");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  function abrirAlta() {
    setCode("");
    setKind("percent");
    setPercent("10");
    setAmount("");
    setCurrency("MXN");
    setExpiresAt("");
    setMaxRedemptions("");
    setFormError(null);
    setOpen(true);
  }

  async function crearCupon(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    // El MISMO esquema del contrato valida antes de viajar.
    const parsed = couponInputSchema.safeParse({
      code,
      kind,
      ...(kind === "percent" ? { percent: Number(percent) } : {}),
      ...(kind === "fixed"
        ? { amountCents: Math.round(Number(amount) * 100), currency }
        : {}),
      ...(expiresAt ? { expiresAt } : {}),
      ...(maxRedemptions ? { maxRedemptions: Number(maxRedemptions) } : {}),
      active: true,
    });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? t("Revisa los datos del cupón"));
      return;
    }

    try {
      await crear.mutateAsync(parsed.data);
      setOpen(false);
    } catch (cause) {
      setFormError(
        cause instanceof Error && cause.message
          ? cause.message
          : t("No se pudo crear el cupón"),
      );
    }
  }

  const items = coupons.data?.items ?? [];
  const total = coupons.data?.total ?? 0;

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Cupones")}
        description={t("Crea códigos de descuento para el checkout. Se apagan, no se borran.")}
      />

      <DataTableFrame aria-busy={coupons.isFetching}>
        {/* El alta vive DENTRO de la tabla, arriba a la derecha (Anahí). */}
        <FilterBar
          actions={
            <Button leftIcon={<Plus />} onClick={abrirAlta}>
              {t("Nuevo cupón")}
            </Button>
          }
        >
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Buscar")}
            <div className="relative w-56">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-ink-3" />
              <Input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder={t("Código…")}
                className="pl-10"
              />
            </div>
          </label>
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Estado")}
            <div className="w-40">
              <Select
                aria-label={t("Estado")}
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value);
                  setPage(1);
                }}
                placeholder={t("Todos")}
                options={[
                  { value: "active", label: t("Activos") },
                  { value: "inactive", label: t("Apagados") },
                ]}
              />
            </div>
          </label>
        </FilterBar>
        {coupons.isPending ? (
          <div className="p-5">
            <SkeletonTable rows={4} columns={6} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={<TicketPercent className="h-5 w-5 text-ds-ink-3" />}
              title={t("Todavía no hay cupones")}
              description={t("Crea el primero y compártelo con tus clientes.")}
              action={
                <Button size="sm" leftIcon={<Plus />} onClick={abrirAlta}>
                  {t("Nuevo cupón")}
                </Button>
              }
            />
          </div>
        ) : (
          <DataTable caption={t("Cupones creados")}>
            <thead>
              <tr>
                <Th>{t("Código")}</Th>
                <Th>{t("Descuento")}</Th>
                <Th align="right">{t("Usos")}</Th>
                <Th>{t("Vigencia")}</Th>
                <Th>{t("Estado")}</Th>
                <Th align="right">{t("Acciones")}</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((coupon) => (
                <Tr key={coupon.id}>
                  <Td className="font-mono text-[13px] font-semibold">{coupon.code}</Td>
                  <Td>{discountLabel(coupon)}</Td>
                  <Td align="right">
                    {coupon.redemptionCount}
                    {coupon.maxRedemptions !== undefined && (
                      <span className="text-ds-ink-3"> / {coupon.maxRedemptions}</span>
                    )}
                  </Td>
                  <Td className="text-ds-ink-3">
                    {coupon.expiresAt ?? t("Sin vencimiento")}
                  </Td>
                  <Td>
                    <Badge variant={coupon.active ? "success" : "neutral"} size="sm" dot>
                      {coupon.active ? t("Activo") : t("Apagado")}
                    </Badge>
                  </Td>
                  <Td align="right">
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={apagar.isPending && apagar.variables?.id === coupon.id}
                      onClick={() =>
                        apagar.mutate({ id: coupon.id, active: !coupon.active })
                      }
                    >
                      {coupon.active ? t("Apagar") : t("Encender")}
                    </Button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </DataTable>
        )}

        {total > 0 && (
          <Pagination page={page} perPage={perPage} total={total} onPageChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        )}
      </DataTableFrame>

      <Modal open={open} onClose={() => setOpen(false)} title={t("Nuevo cupón")}>
        <form onSubmit={(event) => void crearCupon(event)} className="grid gap-4">
          <Input
            label={t("Código")}
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            placeholder="BIENVENIDA10"
            maxLength={24}
            hint={t("Letras, números y guiones. Así lo teclearán tus clientes.")}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label={t("Tipo")}
              value={kind}
              onChange={(event) => setKind(event.target.value as CouponKind)}
              options={COUPON_KINDS.map((value) => ({
                value,
                label: t(KIND_LABELS[value]),
              }))}
            />
            {kind === "percent" ? (
              <Input
                label={t("Descuento %")}
                type="number"
                min={1}
                max={100}
                value={percent}
                onChange={(event) => setPercent(event.target.value)}
              />
            ) : (
              <div className="grid grid-cols-[1fr_auto] items-end gap-2">
                <Input
                  label={t("Monto")}
                  type="number"
                  min={1}
                  step="0.01"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="200.00"
                />
                <div className="w-24">
                  <Select
                    aria-label={t("Moneda")}
                    value={currency}
                    onChange={(event) => setCurrency(event.target.value as Currency)}
                    options={CURRENCIES.map((value) => ({ value, label: value }))}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label={t("Vigencia (opcional)")}
              type="date"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
            />
            <Input
              label={t("Usos máximos (opcional)")}
              type="number"
              min={1}
              value={maxRedemptions}
              onChange={(event) => setMaxRedemptions(event.target.value)}
              placeholder="∞"
            />
          </div>

          {formError && (
            <Alert variant="error" onDismiss={() => setFormError(null)}>
              {formError}
            </Alert>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" type="button" onClick={() => setOpen(false)}>
              {t("Cancelar")}
            </Button>
            <Button type="submit" loading={crear.isPending}>
              {t("Crear cupón")}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}

function discountLabel(coupon: Coupon): string {
  if (coupon.kind === "percent") return `${coupon.percent}%`;
  return coupon.amountCents !== undefined && coupon.currency
    ? `${formatPrice(coupon.amountCents, coupon.currency)} ${coupon.currency}`
    : "—";
}
